/**
 * A money axis: round amounts, labelled as money.
 *
 * `roundAxisTicks` (`components/portal/Chart.tsx`) picks ticks for a **count** — its
 * step list ends at 10,000, which is one hundred dollars in minor units, and above
 * that it doubles the largest step it has. Fed an MRR peak of 975,000 it answers
 * $12,800 / $9,600 / $6,400 / $3,200 / $0, and `analytics.html` draws $10,000 /
 * $7,500 / $5,000 / $2,500 / $0. The steps a money axis is allowed to use are a
 * different list, so this is a different function rather than a wider one — a chart
 * of conversations should not be offered a $2,500 step.
 *
 * Written as a list plus a loop for the same reason `roundAxisTicks` is:
 * `Math.ceil` is lint-banned outside `money.ts` and `dates.ts`, and a written list of
 * the round numbers an axis may use is more honest than a logarithm anyway.
 *
 * The values stay in minor units, which is what the chart shapes plot and what every
 * `Money.amount` on `schemas/readmodels/analytics.ts` carries.
 */

import { money, type Currency } from '@fareast/shared'

import type { AxisTick } from '../../../components/portal/Chart.js'
import type { Formatter } from '../../../format/useFormat.js'
import { WHOLE_AMOUNT } from './render.js'

/**
 * The amounts a money axis may step by, in minor units — $1 to $100,000.
 *
 * The 1 / 2.5 / 5 progression is what the mockups draw: `$2,500` on the MRR chart,
 * `$8k` on the revenue chart, `$1,000` below the movements zero line.
 */
const STEPS = [
  100, 250, 500, 1_000, 2_500, 5_000, 10_000, 25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000,
  2_500_000, 5_000_000, 10_000_000,
] as const

/** How many intervals an axis is drawn with. Four is what every mockup chart has. */
const INTERVALS = 4

/**
 * Five ticks from zero to a round ceiling at or above the peak.
 *
 * The ceiling is a whole number of steps, so the tallest bar never touches the top of
 * the plot and the labels are amounts a person would say out loud.
 */
export function moneyTicks(
  peak: number,
  currency: Currency,
  format: Formatter,
): readonly AxisTick[] {
  const step = stepFor(peak)
  let top = step
  while (top < peak) top += step

  const ticks: AxisTick[] = []
  for (let value = top; value >= 0; value -= step) {
    ticks.push({ value, label: format.money(money(value, currency), WHOLE_AMOUNT) })
  }
  return ticks
}

/** The smallest step whose four intervals cover the peak. */
function stepFor(peak: number): number {
  const found = STEPS.find((step) => step * INTERVALS >= peak)
  if (found !== undefined) return found
  /* Past the list, keep doubling the largest rather than drawing a broken axis. */
  let step: number = STEPS[STEPS.length - 1] ?? 100
  while (step * INTERVALS < peak) step *= 2
  return step
}

/**
 * The currency a set of amounts is in.
 *
 * Every analytics aggregate is already USD-equivalent (`analytics.ts`'s header), so
 * this reads the first amount rather than assuming the letters — a studio that
 * reported in EUR would draw a EUR axis with no change here.
 */
export function currencyOf(amounts: readonly { readonly currency: Currency }[]): Currency {
  return amounts[0]?.currency ?? 'USD'
}
